import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, Button } from '@radix-ui/themes';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import PatientDetail from '../PatientDetail';
import { patientApi } from '../../services/api';
import { usePatientTabs } from './PatientTabsContext';
import { useOrganization } from '../../contexts/OrganizationContext';

// PHA-509: tab body. Fetches fresh details on mount (or when the tab is
// re-opened with a new initialTab — `openNonce` bumps), then renders
// PatientDetail in inline mode so the existing chart UI lives inside the tab.

interface PatientTabContentProps {
  patientId: string;
  initialTab?: number;
  // Bumps when openOrFocus is called for an already-open tab. Used to reset
  // the inner PatientDetail tab to a freshly-supplied `initialTab`.
  openNonce: number;
}

const PatientTabContent: React.FC<PatientTabContentProps> = ({
  patientId,
  initialTab,
  openNonce,
}) => {
  const [patient, setPatient] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  const { openOrFocus, activeTabId, close } = usePatientTabs();
  const { orgId } = useOrganization();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // PHA-527: plumb the active org explicitly so the backend access check
        // sees the same org the user thinks they're in (rather than falling
        // back to session, which can desync with the dropdown).
        const fresh = await patientApi.get(patientId, orgId || undefined);
        if (!cancelled) {
          setPatient(fresh);
          setLoading(false);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(
          e?.errorData?.details ||
            e?.message ||
            'Unable to load this patient. Please try again.',
        );
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // PHA-967: openNonce bumps on every openOrFocus that supplies an initialTab — i.e. an
    // explicit "take me to this patient's <tab>" request. Refetching there is what makes the
    // post-approval / post-draft-save landing show the plan that was just written, even when the
    // tab was already open and holding a stale payload. openOrFocus calls WITHOUT an initialTab
    // (the tab-label sync in handlePatientUpdate) don't bump it, so there's no refetch loop.
  }, [patientId, refetchKey, orgId, openNonce]);

  const handlePatientUpdate = (updated: any) => {
    setPatient(updated);
    // Keep the tab label in sync if name changed.
    openOrFocus({
      patientId,
      firstName: updated?.first_name,
      lastName: updated?.last_name,
      mrn: updated?.mrn,
    });
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: 320 }}>
        <Flex direction="column" align="center" gap="3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent" />
          <Text size="2" className="text-slate-700">
            Loading patient chart…
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (error || !patient) {
    return (
      <Flex direction="column" align="center" gap="3" style={{ padding: 32 }}>
        <ExclamationTriangleIcon width="32" height="32" color="var(--orange-9)" />
        <Text size="3" weight="medium">
          Could not load patient
        </Text>
        <Text size="2" color="gray" align="center">
          {error || 'Patient details unavailable.'}
        </Text>
        <Button onClick={() => setRefetchKey((k) => k + 1)} variant="solid">
          Retry
        </Button>
      </Flex>
    );
  }

  return (
    <Box>
      <PatientDetail
        inline
        patient={patient}
        onPatientUpdate={handlePatientUpdate}
        onClose={() => close(patientId)}
        initialTab={initialTab}
        // openNonce is forwarded so PatientDetail's initialTab effect re-fires
        // on every openOrFocus call (e.g. user acks two readings in a row that
        // both target the same inner tab).
        reopenNonce={openNonce}
        // PHA-565: every open patient tab stays mounted in Dashboard (hidden
        // via display:none for inactive ones), so isOpen is always true for
        // every tab. Drive the dwell hook from `activeTabId === patientId`
        // instead — switching tabs now flushes the deactivated chart and
        // starts a fresh timer on the active one.
        isActive={activeTabId === patientId}
      />
    </Box>
  );
};

export default PatientTabContent;
